import * as React from "react";
import { Grid, Message, Loader, Dimmer, Button } from "semantic-ui-react";
import { useIntl } from "react-intl";
import { getDownloadURL } from "firebase/storage";

import { StickerCard, UploadMultipleElements } from "../../components";
import { StickerServices } from "../../services";
import { useAccountSettings, mediaElementSticket, useSocialCoachSession } from "../../utils";
import * as DTO from "../../interfaces";

import { SettingsType, descriptors } from "./descriptors";
import { InternalGrid } from "../../styling/baseStyle";
import { StickersContainer, StickerListGrid, TabContentGrid, SaveStickersButton } from "./styled";
import { StickerRequest, WebComponents } from "../../interfaces";
import { useState } from "react";

const StickersFC: React.FC = () => {
  const { formatMessage } = useIntl();
  const [loading, setLoading] = React.useState<boolean>(false);
  const { coachId } = useSocialCoachSession();
  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = hasWritePermission(WebComponents.APP_SETTINGS);
  const [stickerRequest, setStickerRequest] = useState<StickerRequest | undefined>(undefined);

  const [stickers, setStickers] = React.useState<DTO.MediaGroup | undefined>(undefined);
  const [stickersDic, setStickersDic] = React.useState<{ [key: string]: DTO.StickerPreview } | undefined>(undefined);
  const [generalErrors, setGeneralErrors] = React.useState<string[]>([]);

  const updateStickers = (): void => {
    setLoading(true);

    if (stickerRequest) {
      StickerServices.update(coachId!.toString(), stickerRequest)
        .catch((e: string) => {
          alert(e.substring(0, 40));
        })
        .finally(() => {
          setLoading(false);
          setStickerRequest(undefined);
        });
    }
  };

  React.useEffect(() => {
    setLoading(true);
    const getStickers = async () => {
      const result = await StickerServices.get(coachId!.toString());

      setStickers(result.stickers);
      if (result.stickers) {
        setStickersDic(() => {
          let dicStickers = {};
          result.stickers!!.media!!.forEach((media, id) => {
            dicStickers = {
              ...dicStickers,
              [id]: {
                uid: id,
                loading: false,
                media,
              },
            };
          });

          return {
            ...dicStickers,
          };
        });
      }

      setLoading(false);
    };

    getStickers().catch(console.error);
  }, [coachId]);

  React.useEffect(() => {
    if (stickersDic) {
      const imageLoading = Object.values(stickersDic).reduce(
        (accumulator, stickerValue) => stickerValue.loading || accumulator,
        false
      );

      if (!imageLoading) {
        prepareRequest();
      }

      setLoading(imageLoading);
    }
  }, [stickersDic]);

  const prepareRequest = () => {
    if (stickersDic) {
      if (Object.values(stickersDic!).length > 100) {
        setGeneralErrors(() => [formatMessage({ ...descriptors[SettingsType.maximunStickersError] })]);
        return;
      }

      const request: DTO.StickerRequest = {
        coachId: coachId!.toString(),
        stickers: {
          ...stickers,
          media: Object.values(stickersDic).map((dicElm: DTO.StickerPreview, index) => {
            return {
              ...dicElm!!.media,
              sortOrder: index,
            } as DTO.Media;
          }),
          type: "MIX",
        },
      };
      setStickerRequest(request);
    }
  };

  const stickerToFile = (file: DTO.SocialFiles): DTO.StickerPreview => {
    file.uploadTask
      ?.then(() => {
        getDownloadURL(file.uploadTask!.snapshot.ref)
          .then(url => {
            setStickersDic(dicStc => {
              if (dicStc && dicStc[file.uid]) {
                return {
                  ...dicStc,
                  [file.uid]: {
                    ...dicStc[file.uid],
                    loadStatus: "SUBMITTED",
                    loading: false,
                    media: mediaElementSticket(url, "png", "IMAGE", Object(dicStc.keys).length),
                  },
                };
              }
              return {
                ...dicStc,
              };
            });
          })
          .catch(() => {
            setStickersDic(dicStc => {
              if (dicStc && dicStc[file.uid]) {
                return {
                  ...dicStc,
                  [file.uid]: {
                    ...dicStc[file.uid],
                    loading: false,
                    loadStatus: "FAILED",
                  },
                };
              }
              return {
                ...dicStc,
              };
            });
          });
      })
      .catch(() => {
        setStickersDic(dicStc => {
          if (dicStc && dicStc[file.uid]) {
            return {
              ...dicStc,
              [file.uid]: {
                ...dicStc[file.uid],
                loadStatus: "FAILED",
              },
            };
          }
          return {
            ...dicStc,
          };
        });
      });

    return {
      loading: true,
      uid: file.uid,
      uploadTask: file.uploadTask,
      thumbnail: file.thumbnail,
    } as DTO.StickerPreview;
  };

  const deleteElement = (key: string): void => {
    setStickersDic(dic => {
      if (dic) {
        delete dic[key];
      }

      return {
        ...dic,
      };
    });
  };

  return (
    <TabContentGrid
      className={!withWritePermission ? "listManagerContent disabledForm" : "listManagerContent tabContent"}
      columns={1}
    >
      {withWritePermission && (
        <Grid.Row>
          <SaveStickersButton onClick={updateStickers} primary loading={loading}>
            Save Stickers
          </SaveStickersButton>
        </Grid.Row>
      )}
      <InternalGrid columns={1} className={"noPadding"}>
        {generalErrors.length > 0 && (
          <Grid.Row columns={1}>
            <Message floating error list={generalErrors} />
          </Grid.Row>
        )}
        <StickersContainer columns={1}>
          <Grid.Column width={16}>
            <UploadMultipleElements
              accept={"image/png, image/jpeg"}
              fileName={`customStickers/${coachId}`}
              nodeName={"sticker"}
              title={"Or Browse"}
              customMessage={"Drag and drop your stickers here to upload"}
              onChangeImage={file => {
                setStickersDic(dic => {
                  return {
                    ...dic,
                    [file.uid]: stickerToFile(file),
                  };
                });
              }}
            />
          </Grid.Column>
        </StickersContainer>

        <StickerListGrid>
          {stickersDic &&
            Object.keys(stickersDic)?.map((key: string) => {
              const stickerPreview = stickersDic[key];
              const uri = stickerPreview.media ? stickerPreview.media.uri : stickerPreview.thumbnail;
              return <StickerCard key={key} uri={uri!!} onDelete={() => deleteElement(key)} />;
            })}
        </StickerListGrid>
      </InternalGrid>
      {loading && (
        <Dimmer active inverted>
          <Loader inverted content="Loading" />
        </Dimmer>
      )}
    </TabContentGrid>
  );
};

export const StickersTab = StickersFC;
